<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import SalesDetailCompanyProfile from './SalesDetailCompanyProfile.vue';
import SalesDetailPartyAddress from './SalesDetailPartyAddress.vue';
import SalesDetailPartyAttorney from './SalesDetailPartyAttorney.vue';
import SalesDetailPartyDocuments from './SalesDetailPartyDocuments.vue';
import SalesDetailPersonProfile from './SalesDetailPersonProfile.vue';
import SalesDetailRepresentative from './SalesDetailRepresentative.vue';

const props = defineProps({
  transaction: { type: Object, required: true },
  role: { type: String, required: true },
});
const { t } = useI18n();
const selectedId = ref(null);
const overflowOpen = ref(false);
const roleLabel = computed(() =>
  props.role === 'vendedor'
    ? t('SALES_PARTIES.SUMMARY.SELLER')
    : t('SALES_PARTIES.SUMMARY.BUYER')
);
const personTitle = computed(() =>
  t('SALES_PARTIES.SUMMARY.PERSON_TITLE', { role: roleLabel.value })
);
const personAddressTitle = computed(() =>
  t('SALES_PARTIES.SUMMARY.PERSON_ADDRESS_TITLE', {
    role: roleLabel.value,
  })
);
const personDocumentsTitle = computed(() =>
  t('SALES_PARTIES.SUMMARY.PERSON_DOCUMENTS_TITLE', {
    role: roleLabel.value,
  })
);
const companyTitle = computed(() => t('SALES_PARTIES.SUMMARY.COMPANY_TITLE'));
const companyAddressTitle = computed(() =>
  t('SALES_PARTIES.SUMMARY.COMPANY_ADDRESS_TITLE')
);
const companyDocumentsTitle = computed(() =>
  t('SALES_PARTIES.SUMMARY.COMPANY_DOCUMENTS_TITLE')
);
const representativeTitle = computed(() =>
  t('SALES_PARTIES.SUMMARY.REPRESENTATIVE_TITLE')
);
const representativeDocumentsTitle = computed(() =>
  t('SALES_PARTIES.SUMMARY.REPRESENTATIVE_DOCUMENTS_TITLE')
);
const parties = computed(() =>
  (props.transaction.party_details || []).filter(
    party => party.party_role === props.role
  )
);
const visibleParties = computed(() => parties.value.slice(0, 7));
const overflowParties = computed(() => parties.value.slice(7));
const selected = computed(
  () =>
    parties.value.find(party => party.id === selectedId.value) ||
    parties.value[0]
);
const overflowSelected = computed(() =>
  overflowParties.value.some(party => party.id === selected.value?.id)
);
const select = party => {
  selectedId.value = party.id;
  overflowOpen.value = false;
};

watch(
  parties,
  value => {
    if (!value.some(party => party.id === selectedId.value)) {
      selectedId.value = value[0]?.id || null;
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="mx-auto grid w-full max-w-[1160px] gap-[15px] py-8 lg:py-14">
    <p
      v-if="!parties.length"
      class="rounded-lais border border-dashed border-lais-border bg-white p-8 text-center text-sm text-lais-text-muted"
    >
      {{ t('SALES.DELIVERY.SUMMARY.PARTIES.EMPTY') }}
    </p>
    <template v-else>
      <nav
        class="flex min-h-24 flex-wrap items-center justify-between gap-4 rounded-lais border border-lais-border-soft bg-white p-6 shadow-lais-card"
        :aria-label="t('SALES.DELIVERY.SUMMARY.PARTIES.ARIA')"
        @keydown.esc="overflowOpen = false"
      >
        <div
          v-for="party in visibleParties"
          :key="party.id"
          class="group relative min-w-0 flex-1 basis-32 lg:max-w-[20%]"
        >
          <button
            type="button"
            class="h-12 w-full truncate rounded-lais-sm px-3 text-sm font-semibold transition focus-visible:shadow-lais-focus"
            :class="
              selected?.id === party.id
                ? 'bg-lais-primary-50 text-lais-primary'
                : 'bg-transparent text-lais-text-body hover:bg-lais-bg-soft'
            "
            :aria-describedby="`sales-summary-party-${party.id}-tooltip`"
            @click="select(party)"
          >
            {{ party.display_name }}
          </button>
          <span
            :id="`sales-summary-party-${party.id}-tooltip`"
            role="tooltip"
            class="pointer-events-none absolute left-1/2 top-full z-20 mt-2 max-w-64 -translate-x-1/2 rounded-lais-sm bg-lais-bg-soft px-2 py-2 text-center text-xs font-normal text-lais-text-muted opacity-0 shadow-lais-card transition group-hover:opacity-100 group-focus-within:opacity-100"
          >
            {{ party.display_name }}
          </span>
        </div>
        <div v-if="parties.length >= 7" class="relative shrink-0">
          <button
            type="button"
            class="inline-flex h-12 items-center gap-1 rounded-lais-sm px-3 text-sm font-semibold transition focus-visible:shadow-lais-focus"
            :class="
              overflowSelected
                ? 'bg-lais-primary-50 text-lais-primary'
                : 'bg-transparent text-lais-text-body hover:bg-lais-bg-soft'
            "
            aria-haspopup="menu"
            :aria-expanded="overflowOpen ? 'true' : 'false'"
            @click="overflowOpen = !overflowOpen"
          >
            <span
              class="i-lucide-chevron-down size-[22px]"
              aria-hidden="true"
            />
            {{
              t('SALES.DELIVERY.SUMMARY.PARTIES.MORE', {
                count: overflowParties.length,
              })
            }}
          </button>
          <div
            v-if="overflowOpen && overflowParties.length"
            role="menu"
            class="absolute right-0 z-30 mt-2 w-64 overflow-hidden rounded-lais border border-lais-border bg-white shadow-lais-pop"
          >
            <button
              v-for="party in overflowParties"
              :key="party.id"
              type="button"
              role="menuitem"
              class="block w-full truncate border-b border-lais-border-soft bg-transparent px-[18px] py-[13px] text-left text-sm font-bold last:border-b-0 hover:bg-lais-bg-soft"
              :class="
                selected?.id === party.id
                  ? 'text-lais-primary'
                  : 'text-lais-text-body'
              "
              @click="select(party)"
            >
              {{ party.display_name }}
            </button>
          </div>
        </div>
      </nav>

      <section
        v-if="selected"
        class="grid gap-4"
        :aria-label="selected.display_name"
      >
        <SalesDetailCompanyProfile
          v-if="selected.party_kind === 'pessoa_juridica'"
          :party="selected"
          summary
          :card-title="companyTitle"
        />
        <SalesDetailPersonProfile
          v-else
          :party="selected"
          summary
          :card-title="personTitle"
        />
        <SalesDetailPartyAddress
          :party="selected"
          :card-title="
            selected.party_kind === 'pessoa_juridica'
              ? companyAddressTitle
              : personAddressTitle
          "
        />
        <SalesDetailPartyAttorney
          v-if="selected.party_kind === 'pessoa_fisica'"
          :party="selected"
          summary
        />
        <SalesDetailPartyDocuments
          :documents="selected.documents || []"
          :party-id="selected.id"
          :transaction-id="transaction.id"
          summary
          :card-title="
            selected.party_kind === 'pessoa_juridica'
              ? companyDocumentsTitle
              : personDocumentsTitle
          "
        />
        <div v-if="selected.representatives?.length" class="grid gap-3">
          <p class="text-sm font-semibold text-lais-text">
            {{ t('SALES.DETAIL.REPRESENTATIVES') }}
          </p>
          <SalesDetailRepresentative
            v-for="representative in selected.representatives"
            :key="representative.id"
            :representative="representative"
            :transaction-id="transaction.id"
            summary
            :profile-title="representativeTitle"
            :documents-title="representativeDocumentsTitle"
          />
        </div>
      </section>
    </template>
  </div>
</template>
